import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { naicsQueryKey } from '@/hooks/federal-contract/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { NAICS } from '@/types/federal-contract/Api'

interface UseNaicsCodesArgs {
  codes: string[]
}

export const fetchNaicsCodes = async (
  codes: Array<string>,
  accessToken: string | undefined | null,
): Promise<PaginatedResponse<NAICS>> => {
  if (codes.length === 0) {
    return { count: 0, has_more: false, data: [] }
  }
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const idString =
    codes
      .slice(0, -1)
      .map((code) => `naics_codes=${code}&`)
      .join('') + `naics_codes=${codes[codes.length - 1]}`
  const url = `${API_URL}/api/v1/naics/?${idString}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return (await res.json()) as PaginatedResponse<NAICS>
}

const selectNaicsCodes = (data: PaginatedResponse<NAICS>) => data.data

export const useNaicsCodes = (args: UseNaicsCodesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const query = useQuery({
    queryFn: () => fetchNaicsCodes(args.codes, accessToken),
    select: selectNaicsCodes,
    queryKey: naicsQueryKey.codes(args),
    staleTime: Infinity,
  })
  return query
}
